// import PhoneIcon from '@/assets/icons/dashboard/phone.svg';
import MessageIcon from '@/assets/icons/dashboard/message.svg';
import MessageMobileIcon from '@/assets/icons/message-2.svg';
import UserIcon from '@/assets/icons/dashboard/user-outlined.svg';
import LogoutIcon from '@/assets/icons/logout-green.svg';
import Logo from '@/assets/icons/dashboard/sidebar-main-logo.svg';
import ArrowDown from '@/assets/icons/arrow-down-green.svg';
import SupportIcon from '@/assets/icons/support-green.svg';

import NavbarDropdown from './navbar-dropdown';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import { CustomersController } from '@/shared/endpoints/customers';
import { ISimpleDropdownOption } from '@/shared/types/interfaces';
import { showScroll } from '@/store/slices/onboarding';
import { Link } from 'react-router-dom';
import { userDetails } from '@/store/slices/onboarding/selectors';
import useResetStore from '@/shared/hooks/useResetStore';

const contactUsLink = 'https://medbox.com/contact-us/';
// const scheduleCallLink = 'https://medbox.com/book/';

// feat: link to schedule call
const supportOptions: ISimpleDropdownOption[] = [
    // {
    //     label: 'Schedule a Call',
    //     icon: PhoneIcon,
    //     link: scheduleCallLink
    // },
    {
        label: 'support@medbox.com',
        icon: MessageMobileIcon,
        link: contactUsLink
    }
];

const Navbar = ({ headerRef, hideTopBg }: { headerRef?: React.RefObject<HTMLDivElement>, hideTopBg?: boolean }) => {
    const dispatch = useAppDispatch();
    const resetStore = useResetStore();
    const user = useAppSelector(userDetails);
    const [isOpen, setIsOpen] = useState(false);
    const [activeMenu, setActiveMenu] = useState<'support' | 'user' | ''>('');
    const userName = user?.userProfile?.firstName + ' ' + user?.userProfile?.lastName;
    const userNameLogo = user?.userProfile?.firstName[0] + '' + user?.userProfile?.lastName[0];

    const handleLogout = useCallback(async () => {
        resetStore();
        window.localStorage.removeItem('takeMoment');
        dispatch(CustomersController.logout());
    }, [dispatch, resetStore]);

    const userOptions = useMemo(
        (): ISimpleDropdownOption[] => [
            {
                label: 'Log out',
                icon: LogoutIcon,
                onClick: handleLogout
            }
        ],
        [handleLogout]
    );

    const activeOptions = useMemo(() => {
        if (!activeMenu) return [];
        else if (activeMenu === 'support') return supportOptions;
        else return userOptions;
    }, [activeMenu, userOptions]);

    const paperStyles = useMemo(() => {
        const maxHeight = (activeOptions?.length || 0) * 60;
        return {
            height: isOpen ? `${maxHeight + 24 + (activeMenu === 'support' ? 60 : 0)}px` : '0',
            transition: 'all 0.4s ease-in-out'
        };
    }, [isOpen, activeOptions?.length, activeMenu]);

    const handleOnMenuOpen = (menu: 'support' | 'user') => {
        setActiveMenu(prev => {
            if (prev === menu) {
                dispatch(showScroll(false));
                setIsOpen(false);
                return '';
            }
            dispatch(showScroll(true));
            setIsOpen(true);
            return menu;
        });
    };

    return (
        <div ref={headerRef} className={`fixed right-0 top-0 z-50 w-full lg:right-navbarOffsetLg lg:top-2.5 lg:w-[calc(100%_-_274px)] lx:!right-navbarOffsetLx lx:!w-[calc(100%_-_337px)] 3xl:!right-navbarOffset3xl 3xl:!w-[calc(100%_-_407px)]`}>
            <div className={`absolute -top-2.5 left-[-300px] hidden h-8 w-[110vw] ${!hideTopBg ? 'bg-[#F2F1EF]' : ''} lg:block`} />

            <div className="relative z-20 flex justify-between rounded-b-[8px] bg-[#FBFBFB] px-5 py-4 shadow-dashboardNavbarMobile lg:justify-end lg:rounded-xl lg:bg-[#fefefe] lg:px-0 lg:py-0 lg:shadow-appNavbar">
                <img src={Logo} alt="" className="max-w-28 lg:hidden" />
                <div className="flex flex-wrap items-center">
                    <div className="relative hidden flex-wrap items-center gap-8 border-r-1 border-grey-300 py-0 pr-7 lg:flex lg:py-4">
                        {/* <div className="flex items-center gap-2">
                            <img src={PhoneIcon} alt="" />
                            <a target="_blank" href={scheduleCallLink} className='font-atkinsonHyperlegible text-lg text-primary-900'>Schedule a Call</a>
                        </div> */}

                        <Link className="flex items-center gap-2" to={contactUsLink} target="_blank">
                            <img src={MessageIcon} alt="" />
                            <span className="font-atkinsonHyperlegible text-lg text-primary-900">support@medbox.com</span>
                        </Link>
                    </div>
                    <div className="hidden lg:block">
                        <NavbarDropdown label={userName} icon={UserIcon} options={userOptions} />
                    </div>
                    <div className="flex flex-wrap items-center gap-1 lg:hidden ms:gap-2">
                        <CollapseTrigger
                            isOpen={isOpen}
                            icon={<img src={SupportIcon} alt="" className="m-[1px]" />}
                            showArrow={false}
                            onClick={() => handleOnMenuOpen('support')}
                            testId="button-navbar-collapse-trigger-support"
                        />
                        <CollapseTrigger
                            isOpen={isOpen}
                            label={
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-700 font-atkinsonHyperlegible text-lg text-white">
                                    {userNameLogo}
                                </div>
                            }
                            onClick={() => handleOnMenuOpen('user')}
                            testId="button-navbar-collapse-trigger-user"
                        />
                    </div>
                </div>
            </div>
            <div
                className="absolute top-[calc(100%_-_24px)] z-10 flex w-full flex-col justify-end overflow-hidden rounded-b-[8px] bg-[#FBFBFB] pt-6 lg:hidden"
                style={paperStyles}
            >
                {activeMenu === 'support' && <h3 className="border-b-1 px-8 py-4 font-atkinsonHyperlegible text-[21px]">How Can We Help?</h3>}
                {activeOptions.map((option, idx) => (
                    <Link
                        target='_blank'
                        className="hover:bg-grey-400 flex w-full cursor-pointer items-center gap-3 px-8 py-4 duration-200 hover:bg-mint-200"
                        key={idx}
                        onClick={() => option.onClick?.()}
                        to={option.link || ''}
                    >
                        <div className='min-w-[30px]'><img src={option.icon} alt="" className='w-[24px] h-[24px]' /></div>
                        <span className="font-atkinsonHyperlegible text-[19px] text-primary-900">{option.label}</span>
                    </Link>
                ))}
            </div>
            <span
                className={`absolute block h-screen w-screen bg-[rgba(0,0,0,0.2)] lg:hidden ${isOpen ? 'block' : 'hidden'}`}
                onClick={() => {
                    dispatch(showScroll(false));
                    setIsOpen(false);
                }}
            ></span>
        </div>
    );
};

const CollapseTrigger = ({
    icon,
    showArrow = true,
    isOpen,
    label,
    onClick,
    testId,
}: {
    testId?: string;
    icon?: ReactNode;
    label?: string | ReactNode;
    onClick?: () => void;
    showArrow?: boolean;
    isOpen: boolean;
}) => {
    return (
        <button
            data-testid={testId || 'button-navbar-collapse'}
            className="rounded-36 flex items-center gap-2 rounded-full border-1 border-[#D6D9D8] bg-white px-1.5 py-1.5 duration-200 hover:bg-mint-200 ms:gap-3"
            onClick={onClick}
        >
            {icon || label}
            {showArrow && <img src={ArrowDown} alt="" className={`duration-200 ${isOpen ? 'rotate-180' : ''} mr-1 sm:mr-0`} />}
        </button>
    );
};

export default Navbar;
